import { Link } from 'react-router-dom';
import type { Device } from '../lib/types';
import { FollowingBadge } from './FollowingBadge';
import { PrimingIcon } from './PrimingIcon';
import { PumpThumb } from './PumpThumb';

/**
 * Single-pump card on the fleet view. Mirrors
 * `Mobile/iOS/AquaStar/Views/Devices/PumpFleetView.swift#PumpFleetCard`:
 *
 *   ●  Pool Pump #1            2,150 RPM
 *      Online                  ⚡ 1,240 W   [S2]
 *
 * Layout:
 *   - Left: status dot + name (large) + status text or "Following · X" line
 *   - Right: headline (RPM / OFF / PRIMING) + watts + preset capsule
 *
 * The whole row is one wide tappable card — easier to read pump names
 * than the previous 3-up grid where long names truncated. iOS uses a
 * single-column layout for the same reason.
 */
export function PumpCard({ device }: { device: Device }) {
  const isFollowing = device.ownership === 'following';
  const isConnected = device.connectionStatus?.isConnected ?? false;
  const isOn = device.deviceStatus === 'ON';
  const isPriming =
    isOn &&
    !!device.selectedSpeed &&
    !['S1', 'S2', 'S3', 'S4'].includes(device.selectedSpeed);
  const presetLabel =
    isOn &&
    device.selectedSpeed &&
    ['S1', 'S2', 'S3', 'S4'].includes(device.selectedSpeed)
      ? device.selectedSpeed
      : null;

  const statusColor = isConnected ? 'var(--aqua-online)' : '#9aa0a6';
  const statusLabel = isConnected ? 'Online' : 'Offline';

  let headline: string;
  let headlineColor: string;
  if (!isConnected) {
    headline = 'OFFLINE';
    headlineColor = 'var(--aqua-offline)';
  } else if (!isOn) {
    headline = 'OFF';
    headlineColor = 'var(--aqua-text-muted)';
  } else if (isPriming) {
    headline = 'PRIMING';
    headlineColor = 'var(--aqua-priming)';
  } else if (typeof device.currentSpeed === 'number') {
    headline = `${device.currentSpeed.toLocaleString()} RPM`;
    headlineColor = 'var(--aqua-text-strong)';
  } else {
    headline = 'ON';
    headlineColor = 'var(--aqua-text-strong)';
  }

  return (
    <Link
      to={`/pump/${encodeURIComponent(device.deviceId)}`}
      className="text-decoration-none"
      style={{ color: 'inherit' }}
    >
      <div
        className="card"
        style={{
          borderColor: 'var(--aqua-border)',
          boxShadow: 'var(--aqua-shadow-sm)',
          transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = 'var(--aqua-shadow-md)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'var(--aqua-shadow-sm)';
        }}
      >
        <div
          className="card-body d-flex align-items-center gap-3"
          style={{ padding: '1rem 1.25rem' }}
        >
          {/* Pump thumbnail — green when online, greyed when offline.
              Replaces the previous solid status dot (a-la iOS parity). */}
          <PumpThumb online={isConnected} size={32} />

          {/* Name + sub-status */}
          <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
            <div
              className="d-flex align-items-center gap-2 flex-wrap"
              style={{ minWidth: 0 }}
            >
              <span
                className="fw-semibold"
                style={{
                  color: 'var(--aqua-text-strong)',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {device.deviceName || device.deviceId}
              </span>
              {isFollowing && (
                <FollowingBadge ownerDisplayName={device.ownerDisplayName} />
              )}
            </div>
            <span
              className="small"
              style={{
                color: isConnected ? 'var(--aqua-online)' : 'var(--aqua-text-muted)',
                fontWeight: 500,
              }}
            >
              {statusLabel}
              {device.modelNumber && (
                <>
                  <span aria-hidden="true" className="mx-1 text-muted">
                    ·
                  </span>
                  <span className="text-muted">{device.modelNumber}</span>
                </>
              )}
            </span>
          </div>

          {/* Headline + footer */}
          <div
            className="d-flex flex-column align-items-end"
            style={{ flexShrink: 0, minWidth: 110 }}
          >
            <span
              className="d-inline-flex align-items-center gap-1"
              style={{
                fontSize: '1.05rem',
                fontWeight: 700,
                lineHeight: 1.1,
                color: headlineColor,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {isPriming && <PrimingIcon size={14} />}
              {headline}
            </span>
            <div
              className="d-flex align-items-center gap-2 mt-1"
              style={{ fontSize: '0.8rem' }}
            >
              {isOn && device.currentWatt ? (
                <span
                  className="d-inline-flex align-items-center gap-1"
                  style={{ color: '#e08600', fontWeight: 600 }}
                >
                  <BoltIcon />
                  {device.currentWatt.toLocaleString()} W
                </span>
              ) : null}
              {presetLabel && (
                <span
                  style={{
                    backgroundColor: 'rgba(1, 157, 220, 0.15)',
                    color: 'var(--aqua-primary)',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    padding: '0.15rem 0.5rem',
                    borderRadius: 999,
                    letterSpacing: 0.5,
                  }}
                >
                  {presetLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function BoltIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M9.5 1 4 9h3.5L6.5 15 12 7H8.5L9.5 1Z" />
    </svg>
  );
}
